import { Box } from "@mui/material";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useMemo, useState } from "react";
import DollarCoinSvg from "@/assets/svg/DollarCoinSvg";
import { formatMoney } from "@/helpers/formatMoney";

const makeCoins = (n: number) =>
  Array.from({ length: n }).map((_, i) => {
    const centered = i - (n - 1) / 2;
    return {
      id: i,
      startX: centered * 6,
      targetX: centered * 16,
      delay: i * 0.05,
      spin: 540 + (i % 3) * 180,
      size: 22 + (i % 2) * 6,
    };
  });

const CoinBurst = ({
  trigger,
  amount,
  count = 6,
}: {
  trigger: number;
  amount?: number;
  count?: number;
}) => {
  const [run, setRun] = useState(0);

  useEffect(() => {
    if (trigger > 0) setRun((r) => r + 1);
  }, [trigger]);

  const coins = useMemo(() => makeCoins(Math.max(1, count)), [count]);

  return (
    <Box
      aria-hidden
      sx={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        overflow: "visible",
        perspective: "600px",
        zIndex: 5,
      }}
    >
      <AnimatePresence>
        {run > 0 && (
          <Box
            key={run}
            component={motion.div}
            sx={{ position: "absolute", inset: 0 }}
            initial={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {amount != null && (
              <Box
                component={motion.div}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 800,
                  fontSize: "15px",
                  color: "#16A34A",
                  textShadow: "0 1px 2px rgba(255,255,255,0.9)",
                  whiteSpace: "nowrap",
                }}
                initial={{ y: 6, opacity: 0, scale: 0.7 }}
                animate={{ y: [-2, -22, -34], opacity: [0, 1, 1, 0], scale: [0.7, 1.15, 1] }}
                transition={{ duration: 1.5, ease: "easeOut", times: [0, 0.2, 0.85, 1] }}
              >
                +${formatMoney(amount)}
              </Box>
            )}

            {coins.map((c) => (
              <Box
                key={c.id}
                component={motion.div}
                sx={{
                  position: "absolute",
                  top: 0,
                  left: "6px",
                  width: c.size,
                  height: c.size,
                  transformStyle: "preserve-3d",
                  filter: "drop-shadow(0 4px 6px rgba(0,0,0,0.18))",
                }}
                initial={{ x: c.startX, y: -46, opacity: 0, scale: 0.5, rotateY: 0 }}
                animate={{
                  x: [c.startX, c.targetX],
                  y: [-46, 2, -6, 0, 2, 0],
                  opacity: [0, 1, 1, 1, 1, 0],
                  scale: [0.5, 1, 1, 1, 1, 0.85],
                  rotateY: [0, c.spin],
                }}
                transition={{
                  duration: 1.5,
                  delay: c.delay,
                  ease: "easeOut",
                  times: [0, 0.35, 0.5, 0.62, 0.8, 1],
                }}
              >
                <Box
                  sx={{ width: "100%", height: "100%", "& svg": { width: "100%", height: "100%" } }}
                >
                  <DollarCoinSvg />
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default CoinBurst;
